import { capitalize } from "@app/string-utils";
import {
  /* For now, we'll enable all of the community modules while we refactor tables
   * across the app. This will temporarily increase the bundle size, but it will
   * allow us to quickly iterate on the table UI without having to worry about
   * which modules are needed. When we're done, we'll come back and only enable
   * the modules we need.
   */
  AllCommunityModule,
  type ColDef,
  type FilterChangedEvent,
  type FirstDataRenderedEvent,
  type GetRowIdParams,
  type GridApi,
  type GridOptions,
  type GridSizeChangedEvent,
  type GridState,
  type IRowNode,
  type ITooltipParams,
  type PaginationChangedEvent,
  type RowSelectionMode,
  type SelectionChangedEvent,
  type SortChangedEvent,
  type SortDirection,
  themeQuartz,
} from "ag-grid-community";
import {
  AgGridProvider,
  AgGridReact,
  type CustomCellRendererProps,
  type CustomHeaderProps,
} from "ag-grid-react";
import cn from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";
import type React from "react";
import { useSearchParams } from "react-router-dom";
import { ButtonIcon } from "../button";
import { DropdownMenu } from "../dropdown-menu";
import {
  IconArrowDown,
  IconArrowLeft,
  IconArrowRight,
  IconArrowUp,
  IconInfo,
  IconSettings,
  IconSwitchVertical,
} from "../icons";
import { InputSearch } from "../input";
import { LoadingSpinner } from "../loading";
import { Switch } from "../switch";
import { useExternalFilter } from "./search";

export type { ColDef, CustomCellRendererProps, RowSelectionMode };

export type DataTableColDef<T> = ColDef<T>;
export type DataTableSortDirection = "asc" | "desc";
export type DataTableSort = {
  field: string;
  direction: DataTableSortDirection;
};
export type DataTableCellRendererProps<T> = CustomCellRendererProps<T> & {
  data: T;
};

/**
 * Shared sizing for a table's ID column. Spread into that column's
 * colDef: `{ ...idColDef, field: "id", valueGetter: ... }`. Override
 * initialWidth/maxWidth afterward for an ID that isn't a short number
 * (e.g. a model id string).
 */
export const idColDef = {
  headerName: "ID",
  initialWidth: 100,
  maxWidth: 100,
  suppressSizeToFit: true,
  // Exempt from the grid's initial fit-to-content auto-size pass, which
  // otherwise measures only cell content and can render this narrower
  // than initialWidth before any resize happens.
  suppressAutoSize: true,
};

// Use the built-in Quartz theme as our base (we'll customize in index.css)
const aptibleTheme = themeQuartz;

const LoadingOverlayComponent = () => {
  return (
    <div className="flex items-center justify-center h-full pt-12">
      <LoadingSpinner variant="lg" />
    </div>
  );
};

const SortIndicator = ({ sort }: { sort: SortDirection | undefined }) => (
  <span className="ag-sort-indicator-container">
    <span
      className={cn("ag-sort-indicator-icon ag-sort-ascending-icon", {
        "ag-hidden": sort !== "asc",
      })}
    >
      <IconArrowUp
        variant="sm"
        className="h-3 w-3"
        strokeWidth="3"
        color="currentColor"
      />
    </span>
    <span
      className={cn("ag-sort-indicator-icon ag-sort-descending-icon", {
        "ag-hidden": sort !== "desc",
      })}
    >
      <IconArrowDown
        variant="sm"
        className="h-3 w-3"
        strokeWidth="3"
        color="currentColor"
      />
    </span>
    <span
      className={cn("ag-sort-indicator-icon ag-sort-none-icon", {
        "ag-hidden": sort != null,
      })}
    >
      <IconSwitchVertical
        variant="sm"
        className="h-4 w-4"
        strokeWidth="2"
        color="currentColor"
      />
    </span>
  </span>
);

const HeaderComponent = <T,>({
  displayName,
  column,
  progressSort,
  enableSorting,
}: CustomHeaderProps<T>) => {
  const colDef = column.getColDef() as ColDef<T>;
  const tooltip = colDef.headerTooltip;
  const [sort, setSort] = useState<SortDirection | undefined>(column.getSort());

  useEffect(() => {
    const onSortChanged = () => setSort(column.getSort());
    column.addEventListener("sortChanged", onSortChanged);
    return () => column.removeEventListener("sortChanged", onSortChanged);
  }, [column]);

  const onSort = (
    event:
      | React.MouseEvent<HTMLDivElement>
      | React.KeyboardEvent<HTMLDivElement>,
  ) => {
    if (!enableSorting) return;
    progressSort?.(event.shiftKey);
  };

  return (
    <div
      className={cn("ag-cell-label-container", {
        "ag-header-cell-sorted-asc": sort === "asc",
        "ag-header-cell-sorted-desc": sort === "desc",
      })}
      onClick={onSort}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSort(event);
        }
      }}
      role={enableSorting ? "button" : undefined}
      tabIndex={enableSorting ? 0 : undefined}
      aria-label={enableSorting ? `Sort by ${displayName}` : undefined}
    >
      <div className="ag-header-cell-label">
        <span className="ag-header-cell-text">{displayName}</span>
        {tooltip && (
          <span className="ag-header-cell-tooltip-icon">
            <IconInfo variant="sm" color="currentColor" />
          </span>
        )}
        {enableSorting && <SortIndicator sort={sort} />}
      </div>
    </div>
  );
};

/** The grid's tooltip box, for a column whose tooltip needs richer content. */
export const DataTableTooltip = ({
  children,
}: { children: React.ReactNode }) => (
  <div className="shadow rounded-md break-normal text-sm text-left bg-black text-white px-3 py-2 w-96 transition-none">
    {children}
  </div>
);

const TooltipComponent = <T,>({ value }: ITooltipParams<T>) => (
  <DataTableTooltip>{value}</DataTableTooltip>
);

// Grid options shared between DataTable and ControlledDataTable.
const baseGridOptions: GridOptions = {
  // Theme the grid to match our design system.
  theme: aptibleTheme,

  loadingOverlayComponent: LoadingOverlayComponent,

  // Render tooltips (and other popups) on document.body instead of inside
  // the grid's own DOM, so they aren't clipped by an ancestor's overflow.
  popupParent: document.body,

  // Set the default configuration for all columns.
  defaultColDef: {
    // Set the column height to the height of the cell content.
    autoHeight: true,
    // Clicking the column header should cycle between ascending and
    // descending order (a third click does not return to no sort).
    sortingOrder: ["asc", "desc"],
    // Always show the unsorted icon when the column is not sorted.
    unSortIcon: true,
    // Use a custom tooltip component that more closely matches our design system.
    tooltipComponent: TooltipComponent,
    // Use a custom header component that more closely matches our design system.
    headerComponent: HeaderComponent,
    // Prevent the column from being pinned.
    lockPinned: true,
  },

  // Show tooltips after a 250ms delay when the cursor hovers over a cell.
  tooltipShowDelay: 250,

  // Don't close the tooltip when the user hovers on it.
  tooltipInteraction: true,

  // Allow the user to select the text in a cell by clicking and dragging the mouse.
  enableCellTextSelection: true,

  // Disable most of the animations for consistency with the rest of the UI.
  animateRows: false,
  animateColumnResizing: false,
  suppressColumnMoveAnimation: true,

  // Prevent the columns from being hidden when the user drags them off the table.
  suppressDragLeaveHidesColumns: true,

  // Allow the table height to be determined by the content (i.e., display all
  // rows in the current page instead of making the table scrollable).
  domLayout: "autoHeight",

  // Automatically resize the columns to fit the contents, and then
  // proportionally scale the column widths to fit the container.
  autoSizeStrategy: {
    type: "fitCellContents",
    scaleUpToFitGridWidth: true,
    skipHeader: true,
  },

  // Maintain the width of the grid when the columns are resized.
  colResizeDefault: "shift",

  // Automatically resize the columns to fit the container when the grid
  // container is resized (e.g., if the user resizes the viewport).
  onGridSizeChanged: ({ api }: GridSizeChangedEvent) => {
    api.sizeColumnsToFit();
  },

  // Don't scroll to the top of the table when new data is loaded.
  suppressScrollOnNewData: true,
};

// ag-grid renders no checkbox column for a bare mode string, only for the object.
const rowSelectionGridOptions = <T,>(
  rowSelection: RowSelectionMode | undefined,
  onSelectionChanged: ((rows: T[]) => void) | undefined,
  rowId: ((row: T) => string) | undefined,
): GridOptions<T> => {
  if (!rowSelection) return {};

  return {
    rowSelection:
      rowSelection === "multiRow"
        ? { mode: "multiRow", selectAll: "filtered" }
        : { mode: "singleRow" },

    selectionColumnDef: { suppressSizeToFit: true },

    ...(rowId
      ? { getRowId: ({ data }: GetRowIdParams<T>) => rowId(data) }
      : {}),

    onSelectionChanged: ({ api }: SelectionChangedEvent<T>) => {
      if (!onSelectionChanged) return;
      onSelectionChanged(api.getSelectedRows());
    },
  };
};

const applyInitialSelection = <T,>(
  api: GridApi<T>,
  rowSelection: RowSelectionMode | undefined,
  isRowSelected: ((row: T) => boolean) | undefined,
) => {
  if (!rowSelection || !isRowSelected) return;

  const nodes: IRowNode<T>[] = [];
  api.forEachNode((node) => {
    if (node.data && isRowSelected(node.data)) {
      nodes.push(node);
    }
  });
  api.setNodesSelected({ nodes, newValue: true });
};

const PaginationControls = ({
  isLoading,
  currentPage,
  totalPages,
  totalResults,
  getPreviousPage,
  getNextPage,
}: {
  isLoading?: boolean;
  currentPage: number;
  totalPages?: number;
  totalResults?: number;
  getPreviousPage: () => void;
  getNextPage: () => void;
}) => {
  return (
    <div className="flex flex-row items-center gap-2 text-gray-500">
      <div className="flex items-center gap-2 whitespace-nowrap">
        Page {currentPage} of{" "}
        <LoadingSpinner
          show={isLoading}
          className="inline-block"
          variant="sm"
        />
        {!isLoading && (totalPages || 1)}
      </div>
      <div className="flex flex-row items-center gap-2">
        <ButtonIcon
          variant="white"
          icon={<IconArrowLeft color="#111920" variant="sm" />}
          size="xs"
          disabled={isLoading || currentPage <= 1}
          onClick={(e) => {
            e.preventDefault();
            getPreviousPage();
          }}
        />
        <ButtonIcon
          variant="white"
          icon={<IconArrowRight color="#111920" variant="sm" />}
          size="xs"
          disabled={isLoading || currentPage >= (totalPages ?? 0)}
          onClick={(e) => {
            e.preventDefault();
            getNextPage();
          }}
        />
        <div className="whitespace-nowrap">
          <div className="flex items-center gap-2">
            <LoadingSpinner
              show={isLoading}
              className="inline-block"
              variant="sm"
            />
            {!isLoading && (totalResults ?? 0)} results
          </div>
        </div>
      </div>
    </div>
  );
};

const ColumnControls = <T,>({
  colDefs,
  onVisibilityChange,
}: {
  colDefs: ColDef<T>[];
  onVisibilityChange: (columnId: string, visible: boolean) => void;
}) => {
  const getColumnId = (col: ColDef<T>) => (col.colId || col.field) as string;
  const visibleColDefs = useMemo(() => {
    return colDefs.filter((col) => col.hide !== true && col.headerName);
  }, [colDefs]) as ColDef<T>[];

  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >(
    visibleColDefs.reduce(
      (acc, col) => {
        // Skip hidden columns.
        if (col.hide === true) return acc;

        return {
          ...acc,
          [getColumnId(col)]: col.initialHide !== true,
        };
      },
      {} as Record<string, boolean>,
    ),
  );

  const toggleColumnVisibility = (columnId: string, checked: boolean) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [columnId]: checked,
    }));
    onVisibilityChange(columnId, checked);
  };

  return (
    <DropdownMenu
      variant="white"
      label={<IconSettings color="#111920" variant="sm" />}
      items={[
        ...visibleColDefs.map((col, index) => {
          const columnId = getColumnId(col);

          return {
            id: columnId,
            label: (
              <div className="flex items-center gap-2">
                <Switch
                  disabled={index === 0}
                  ariaLabel={`Toggle visibility of ${col.headerName}`}
                  checked={columnVisibility[columnId]}
                  onChange={(checked) =>
                    toggleColumnVisibility(columnId, checked)
                  }
                />
                <span>{col.headerName}</span>
              </div>
            ),
          };
        }),
      ]}
    />
  );
};

interface BaseDataTableProps<T> {
  /** The data to display in the table. */
  rowData: T[];
  /** Column definitions for the table. */
  colDefs: ColDef<T>[];
  /** Whether to show the search input. Defaults to true. */
  searchable?: boolean;
  /** Show a loading overlay while data is being fetched. */
  isLoading?: boolean;
  /** Additional CSS class for the grid container. */
  className?: string;
}

// Not on ControlledDataTable: its rows arrive after the seed, one page at a time.
export interface DataTableProps<T> extends BaseDataTableProps<T> {
  /** Render selection checkboxes. Omit to disable row selection entirely. */
  rowSelection?: RowSelectionMode;
  /** Called with every currently selected row whenever the selection changes. */
  onSelectionChanged?: (rows: T[]) => void;
  /** Seeds the selection when the data first renders. Re-applied on remount. */
  isRowSelected?: (row: T) => boolean;
  /** Stable row identity. Without it, selection is lost whenever `rowData` does. */
  rowId?: (row: T) => string;
  /** Number of rows per page. Defaults to 25. */
  pageSize?: number;
  /** Whether to sync the query params with the grid state. Defaults to false. */
  syncQueryParams?: boolean;
  /** Prefix for the query params. Defaults to "". */
  queryParamPrefix?: string;
  /** Initial search text. Applied on mount; overridden by query params when syncQueryParams is true. */
  defaultSearch?: string;
}

export interface ControlledDataTableProps<T> extends BaseDataTableProps<T> {
  /** The current page number (1-indexed). */
  page: number;
  /** Total number of pages available. */
  totalPages: number;
  /** Total number of items across all pages. */
  totalItems: number;
  /** Called when the user navigates to the previous page. */
  onPrev: () => void;
  /** Called when the user navigates to the next page. */
  onNext: () => void;
  /** Called when the search text changes. */
  onSearch?: (text: string) => void;
  /** Called when the user changes the sort. Receives null when sort is cleared. */
  onSort?: (sort: DataTableSort | null) => void;
}

const getQueryParamName = (
  prefix: string,
  name: "page" | "sortBy" | "sortDir" | "search",
) => {
  return [prefix, prefix ? capitalize(name) : name].join("");
};

export function DataTable<T>({
  rowData,
  colDefs,
  searchable = true,
  isLoading = false,
  className,
  rowSelection,
  onSelectionChanged,
  isRowSelected,
  rowId,
  pageSize = 25,
  syncQueryParams = false,
  queryParamPrefix = "",
  defaultSearch,
}: DataTableProps<T>) {
  // We're using a ref here (despite our preference to avoid them) in order to
  // stay consistent with ag-grid's examples and patterns.
  const gridRef = useRef<AgGridReact<T>>(null);

  /** Query params for syncing the table state to the URL.
   *
   * Changes to the table state will be synced to the URL query params. If the
   * page is reloaded, this state will be read from the URL and applied to the
   * grid's initial state.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = getQueryParamName(queryParamPrefix, "page");
  const pageParamValue = Number.parseInt(
    searchParams.get(pageParam) || "1",
    10,
  );
  const sortByParam = getQueryParamName(queryParamPrefix, "sortBy");
  const sortByParamValue = searchParams.get(sortByParam) || undefined;
  const sortDirParam = getQueryParamName(queryParamPrefix, "sortDir");
  const sortDirParamValue = searchParams.get(sortDirParam) as
    | NonNullable<SortDirection>
    | undefined;
  const searchParam = getQueryParamName(queryParamPrefix, "search");
  const searchParamValue = searchParams.get(searchParam) || "";

  // Start from the query params rather than syncing them in afterwards, which
  // would let the sync below write stale defaults over them on mount.
  const [page, setPage] = useState(syncQueryParams ? pageParamValue : 1);
  const [sortBy, setSortBy] = useState<string | undefined>(
    syncQueryParams ? sortByParamValue : undefined,
  );
  const [sortDir, setSortDir] = useState<SortDirection | undefined>(
    syncQueryParams ? sortDirParamValue : undefined,
  );
  const [searchText, setSearchText] = useState(
    syncQueryParams && searchParams.has(searchParam)
      ? searchParamValue
      : (defaultSearch ?? ""),
  );

  const {
    columnFilters,
    quickFilter,
    isExternalFilterPresent,
    doesExternalFilterPass,
  } = useExternalFilter(searchText, colDefs, !!rowSelection);

  // Trigger a filter change when the search text changes to a value that
  // includes column filtering syntax (e.g., "handle:sbx-eric").
  useEffect(() => {
    if (!gridRef.current?.api) return;
    gridRef.current.api.onFilterChanged();
  }, [columnFilters]);

  const [totalResults, setTotalResults] = useState<number | undefined>(
    undefined,
  );
  const [totalPages, setTotalPages] = useState<number | undefined>(undefined);

  // Initialize the grid state from the query params, if syncQueryParams is
  // enabled. We only do this once to prevent a flash between the data loading
  // and the grid state being applied.
  const initialGridState = useMemo<GridState | undefined>(() => {
    if (!syncQueryParams) return undefined;

    const page = pageParamValue - 1;
    const sortBy = sortByParamValue;
    const sortDir = sortDirParamValue;

    return {
      pagination: {
        page: page,
        pageSize: pageSize,
      },
      sort:
        sortBy && sortDir
          ? {
              sortModel: [
                { colId: sortBy, sort: sortDir as NonNullable<SortDirection> },
              ],
            }
          : undefined,
      partialColumnState: true,
    };
  }, []);

  // We generate a new key to force a re-render of the grid when the component remounts,
  // so that we can re-apply the initial grid state.
  const [remountKey, setRemountKey] = useState(new Date().getTime().toString());
  useEffect(() => {
    setRemountKey(new Date().getTime().toString());
  }, [rowData.length]);

  // On change: sync the table state to the query params whenever table state changes, if syncQueryParams is enabled.
  useEffect(() => {
    if (!syncQueryParams) return;

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);

        Object.entries({
          [pageParam]: page > 1 ? page.toString() : "",
          [sortByParam]: sortBy || "",
          [sortDirParam]: sortDir || "",
          [searchParam]: searchText !== (defaultSearch ?? "") ? searchText : "",
        }).forEach(([key, value]) => {
          if (value !== "") {
            next.set(key, value.toString());
          } else {
            next.delete(key);
          }
        });

        return next;
      },
      { replace: true },
    );
  }, [page, sortBy, sortDir, searchText]);

  const handleColumnVisibilityChange = (columnId: string, visible: boolean) => {
    if (!gridRef.current?.api) return;
    gridRef.current.api.setColumnsVisible([columnId], visible);

    // Automatically resize the other visible columns to fit the container.
    gridRef.current.api.sizeColumnsToFit();
  };

  const gridOptions = useMemo<GridOptions<T>>(
    () => ({
      ...baseGridOptions,
      ...rowSelectionGridOptions<T>(rowSelection, onSelectionChanged, rowId),
      loading: isLoading,
      rowData: rowData,
      columnDefs: colDefs,

      // Suppress horizontal scrollbar flash if we're loading, or there's no data.
      suppressHorizontalScroll: isLoading || !rowData.length,

      // Enable client-side pagination, but hide the built-in pagination panel so
      // we can provide our own controls.
      pagination: true,
      paginationPageSize: pageSize,
      suppressPaginationPanel: true,

      // Set the initial grid state so that pagination and sorting are applied
      // when the grid is first rendered (preventing a flash).
      initialState: initialGridState,

      quickFilterText: quickFilter,
      isExternalFilterPresent,
      doesExternalFilterPass,

      onFirstDataRendered: ({ api }: FirstDataRenderedEvent<T>) => {
        applyInitialSelection(api, rowSelection, isRowSelected);

        setTotalResults(api.getDisplayedRowCount());
        setTotalPages(Math.ceil(api.getDisplayedRowCount() / pageSize));

        // Clamp the page to the total pages.
        setPage((prev) => {
          return Math.min(
            Math.max(1, prev),
            Math.ceil(api.getDisplayedRowCount() / pageSize),
          );
        });
      },

      onFilterChanged: ({ api }: FilterChangedEvent<T>) => {
        setTotalResults(api.getDisplayedRowCount());
        setTotalPages(Math.ceil(api.getDisplayedRowCount() / pageSize));
      },

      onPaginationChanged: ({ api, newPage }: PaginationChangedEvent<T>) => {
        if (newPage) {
          setPage(api.paginationGetCurrentPage() + 1);
        }
      },

      onSortChanged: ({ api }: SortChangedEvent<T>) => {
        const sortedCol = api.getColumnState().find((c) => c.sort);
        if (sortedCol) {
          setSortBy(sortedCol.colId);
          setSortDir(sortedCol.sort as SortDirection);
        } else {
          setSortBy(undefined);
          setSortDir(undefined);
        }
      },
    }),
    [
      rowData,
      colDefs,
      isLoading,
      pageSize,
      quickFilter,
      rowSelection,
      onSelectionChanged,
      isRowSelected,
      rowId,
    ],
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <PaginationControls
          isLoading={isLoading}
          currentPage={page}
          totalPages={totalPages}
          totalResults={totalResults}
          getPreviousPage={() =>
            gridRef.current?.api.paginationGoToPreviousPage()
          }
          getNextPage={() => gridRef.current?.api.paginationGoToNextPage()}
        />
        <div className="flex items-center gap-2">
          {searchable && (
            <InputSearch
              search={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search..."
            />
          )}
          <ColumnControls
            colDefs={colDefs}
            onVisibilityChange={handleColumnVisibilityChange}
          />
        </div>
      </div>
      <AgGridProvider modules={[AllCommunityModule]}>
        <div
          className={cn("min-w-0 w-full h-full ag-theme-aptible", className)}
        >
          <AgGridReact<T>
            // We need to key the grid to force a re-render when the row data
            // populates. The isLoading state isn't reliable enough, because it
            // will be true for a short time after the row data populates.
            key={`${remountKey}-${rowData.length}`}
            ref={gridRef}
            {...gridOptions}
          />
        </div>
      </AgGridProvider>
      {(totalPages ?? 1) > 1 && (
        <PaginationControls
          isLoading={isLoading}
          currentPage={page}
          totalPages={totalPages}
          totalResults={totalResults}
          getPreviousPage={() =>
            gridRef.current?.api.paginationGoToPreviousPage()
          }
          getNextPage={() => gridRef.current?.api.paginationGoToNextPage()}
        />
      )}
    </div>
  );
}

export function ControlledDataTable<T>({
  rowData,
  colDefs,
  searchable = true,
  isLoading,
  className,
  page,
  totalPages,
  totalItems,
  onPrev,
  onNext,
  onSearch = () => {},
  onSort,
}: ControlledDataTableProps<T>) {
  // We're using a ref here (despite our preference to avoid them) in order to
  // stay consistent with ag-grid's examples and patterns.
  const gridRef = useRef<AgGridReact<T>>(null);

  const [search, setSearch] = useState("");

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setSearch(text);
    onSearch(text);
  };

  const handleColumnVisibilityChange = (columnId: string, visible: boolean) => {
    if (!gridRef.current) return;
    gridRef.current.api.setColumnsVisible([columnId], visible);

    // Automatically resize the other visible columns to fit the container.
    gridRef.current.api.sizeColumnsToFit();
  };

  const gridOptions = useMemo<GridOptions<T>>(
    () => ({
      ...baseGridOptions,
      loading: isLoading,
      rowData: rowData,
      columnDefs: colDefs,

      // Suppress horizontal scrollbar flash if we're loading, or there's no data.
      suppressHorizontalScroll: isLoading || !rowData.length,

      // Extend the base defaultColDef to suppress local sorting so the
      // server-sorted rowData is displayed in its original order.
      defaultColDef: {
        ...(baseGridOptions.defaultColDef as ColDef<T>),
        ...(onSort ? { comparator: () => 0 } : {}),
      },

      // Delegate sort changes to the caller.
      onSortChanged: ({ api }: SortChangedEvent<T>) => {
        if (!onSort) return;
        const sortedCol = api.getColumnState().find((c) => c.sort);
        onSort(
          sortedCol
            ? {
                field: sortedCol.colId,
                direction: sortedCol.sort as DataTableSortDirection,
              }
            : null,
        );
      },
    }),
    [rowData, colDefs, isLoading, onSort],
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <PaginationControls
          isLoading={isLoading}
          currentPage={page}
          totalPages={totalPages}
          totalResults={totalItems}
          getPreviousPage={onPrev}
          getNextPage={onNext}
        />
        <div className="flex items-center gap-2">
          {searchable && (
            <InputSearch
              search={search}
              onChange={handleSearchChange}
              placeholder="Search..."
            />
          )}
          <ColumnControls
            colDefs={colDefs}
            onVisibilityChange={handleColumnVisibilityChange}
          />
        </div>
      </div>
      <AgGridProvider modules={[AllCommunityModule]}>
        <div
          className={cn("min-w-0 w-full h-full ag-theme-aptible", className)}
        >
          <AgGridReact<T> ref={gridRef} {...gridOptions} />
        </div>
      </AgGridProvider>
      {(totalPages ?? 1) > 1 && (
        <PaginationControls
          isLoading={isLoading}
          currentPage={page}
          totalPages={totalPages}
          totalResults={totalItems}
          getPreviousPage={onPrev}
          getNextPage={onNext}
        />
      )}
    </div>
  );
}
